import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DataGrid, Column, Paging, Pager, Sorting, FilterRow, HeaderFilter, Toolbar, Item } from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import "./ReviewCustomers.css";

import ReviewCustomersEditForm from "./ReviewCustomersEditForm";
import { CustomerModel } from "@/shared/models/Customer";
import { selectCustomersDataSource, setCustomersDataSource } from "@/redux/customersSlice";
import { ReviewCustomersApi } from "@/shared/api/ReviewCustomers.api";
import { store } from "@/store/store";
import { Utilities } from "@/shared/utils/utilities";
import { setToastInfo } from "@/redux/shareSlice";
import { setSalesPersonDataSource } from "@/redux/salesRepSlice";
import { Pages } from "@/shared/constants";
import { usePopupDisplay } from "@/hooks/usePopupDisplay";
import NextButton from "@/components/NextButton";
import { ReviewCustomersGridColumns } from "@/shared/business/ReviewCustomers";
import { DataGridCustomizeCell, DataGridCustomizeCellFilter, DataGridCustomizeCellSort } from "./DataGridCustomizeCell";
import UpdateRunHistoryForm from "./UpdateRunHistoryForm";

const ReviewCustomers = () => {
  const { popupVisible, showPopUp, hidePopup } = usePopupDisplay();
  const reviewCustomersDataSource = useSelector(selectCustomersDataSource);

  const [showActives, setShowActives] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerModel>(new CustomerModel());
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCustomerEdit, setIsCustomerEdit] = useState(true);

  //  Fetch Customer Data from API on Component Mount
  useEffect(() => {
    if (reviewCustomersDataSource.length > 0) {
      return;
    }

    fetchData();
  }, [showActives, reviewCustomersDataSource]);

  const fetchData = async () => {
    var data = await ReviewCustomersApi.getCustomers();
    if (!data) {
      return;
    }
    if (showActives) {
      data = data.filter((x) => x.IncludeInNewSurchargeEvents === true);
    }

    store.dispatch(setCustomersDataSource(data));

    
    const salesRepList = Array.from(
      new Set([
        ...data.map((x: CustomerModel) => x.PrimarySalesRep).filter(Boolean),
        "Placeholder Sales Rep"
      ])
    );

    store.dispatch(setSalesPersonDataSource(salesRepList));
  };

  const showActiveInactiveClick = () => {
    setShowActives((prev) => !prev);
    store.dispatch(setCustomersDataSource([]));
  };

  const handleExportClick = async () => {
    var fileName = await ReviewCustomersApi.exportCustomerList();
    !Utilities.isNullOrEmptyString(fileName) &&
      store.dispatch(setToastInfo(`Customer list is downloaded successfully as ${fileName}.`));
  };

  const handleCreateCustomerClick = () => {
    setIsCustomerEdit(true);
    setIsEditMode(false);
    setSelectedCustomer(new CustomerModel());
    showPopUp();
  };

  const handleUpdateRunHistoryClick = () => {
    setIsCustomerEdit(false);
    showPopUp();
  };

  const handleRowClick = (e: any) => {
    setIsCustomerEdit(true);
    const customer = e.data as CustomerModel;
    setIsEditMode(true);
    setSelectedCustomer({ ...customer });
    showPopUp();
  };

  const handleFormClose = async (reloadData: boolean) => {
    hidePopup();
    if (reloadData) {
      await fetchData();
    }
  };

  return (
    <>
      <div>
        <DataGrid<CustomerModel>
          dataSource={reviewCustomersDataSource}
          showBorders={true}
          keyExpr="ShipTo"
          focusedRowEnabled={true}
          repaintChangesOnly={true}
          allowColumnResizing={true}
          allowColumnReordering={true}
          hoverStateEnabled={true}
          wordWrapEnabled={false}
          columnAutoWidth={true}
          noDataText="No customers found"
          onRowClick={handleRowClick}
        >
          {/*  Enable Filtering, Sorting, and Paging */}
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Sorting mode="multiple" />
          {/* <Paging defaultPageSize={10} /> */}

          <Paging enabled={true} defaultPageSize={75} />

          {/*  Configure Pager */}
          <Pager
            visible={true}
            allowedPageSizes={[10, 25, 50, 75]} // Options for page size
            showPageSizeSelector={true}
            showInfo={true} // Show "Page X of Y"
            showNavigationButtons={true}
          />

          <Column
            dataField={"ShipTo"}
            dataType="number"
            filterOperations={["contains", "startswith", "endswith"]}
            caption={"SAP SHIP TO"}
            fixed={true}
            fixedPosition={"left"}
            cssClass={"fixed-column-style"}
            cellRender={(cellData) => DataGridCustomizeCell(cellData, { dataField: "ShipTo" } as any)}
          />

          {/* {dynamic binding} */}
          {ReviewCustomersGridColumns.map((item, key) => (
            <Column
              key={key}
              dataField={item.dataField}
              filterOperations={["contains", "startswith", "endswith"]}
              caption={item.caption}
              fixed={item.fixed}
              fixedPosition={item.fixedPosition}
              allowFiltering={item.allowFiltering === undefined ? true : item.allowFiltering}
              cssClass={item.cssClass}
              cellRender={(cellData) => DataGridCustomizeCell(cellData, item)}
              calculateFilterExpression={(filterValue, selectedFilterOperation) => DataGridCustomizeCellFilter(filterValue, selectedFilterOperation!, item)}
              calculateSortValue={(data) => DataGridCustomizeCellSort(data, item)}
            />
          ))}

          <Toolbar>
            <Item location="after" locateInMenu="auto">
              <Button type="default" text={showActives ? "SHOW INACTIVE" : "HIDE INACTIVE"} onClick={showActiveInactiveClick}></Button>
            </Item>
            <Item location="after" locateInMenu="auto">
              <Button type="default" icon="export" text="EXPORT CUSTOMER LIST TO CSV FILE" onClick={handleExportClick}></Button>
            </Item>
            <Item location="after">
              <Button icon="add" text="CREATE CUSTOMER" type="default" onClick={handleCreateCustomerClick}></Button>
            </Item>
            <Item location="after">
              <Button type="default" text="SHOW SAP / LHYST UPDATE RUN HISTORY" onClick={handleUpdateRunHistoryClick}></Button>
            </Item>
            <Item name="searchPanel" location="after" />
          </Toolbar>
        </DataGrid>

        <div className="mt-2 button-height">
          <NextButton nextPage={Pages.ReviewProductionIndices} />
        </div>
      </div>
      <div>
        {/* Render Popup Form */}
        {popupVisible &&
          (isCustomerEdit ? (
            <ReviewCustomersEditForm visible={popupVisible} customerData={selectedCustomer} eidt={isEditMode} closeForm={handleFormClose} />
          ) : (
            <UpdateRunHistoryForm visible={popupVisible} closeForm={handleFormClose}></UpdateRunHistoryForm>
          ))}
      </div>
    </>
  );
};

export default ReviewCustomers;
